<template>
  <Transition name="feedback-fade">
    <div
      v-if="visible"
      class="feedback-backdrop"
      @click.self="close"
      @keydown.esc="close"
      role="dialog"
      aria-modal="true"
      :aria-label="STRINGS.aria.labels.feedbackForm"
    >
      <div class="feedback-panel">
        <!-- Header -->
        <div class="feedback-header">
          <div class="feedback-header-left">
            <i class="pi pi-comment feedback-header-icon" aria-hidden="true"></i>
            <h2 class="feedback-title">{{ STRINGS.feedback.title }}</h2>
          </div>
          <button
            class="feedback-close"
            :disabled="isSubmitting"
            :aria-label="STRINGS.aria.labels.closeFeedback"
            @click="close"
          >
            <i class="pi pi-times"></i>
          </button>
        </div>

        <!-- Success state -->
        <div v-if="isSuccess" class="success-body">
          <div class="success-icon">
            <i class="pi pi-check"></i>
          </div>
          <p class="success-title">{{ STRINGS.feedback.success.title }}</p>
          <p class="success-message">{{ STRINGS.feedback.success.message }}</p>
          <button class="btn btn-ghost success-dismiss" @click="close">
            {{ STRINGS.feedback.buttons.close }}
          </button>
        </div>

        <!-- Form body -->
        <template v-else>
          <div class="feedback-body" :class="{ 'feedback-body--submitting': isSubmitting }">
            <p class="feedback-prompt">{{ STRINGS.feedback.prompt }}</p>

            <!-- Type selector -->
            <div
              class="type-selector"
              :aria-label="STRINGS.aria.labels.feedbackType"
              role="radiogroup"
            >
              <button
                v-for="opt in categoryOptions"
                :key="opt.value"
                class="type-option"
                :class="{ active: category === opt.value }"
                role="radio"
                :aria-checked="category === opt.value"
                @click="category = opt.value"
              >
                <i :class="opt.icon"></i>
                <span>{{ opt.label }}</span>
              </button>
            </div>

            <!-- Subject -->
            <div class="field">
              <label class="field-label">{{ STRINGS.feedback.labels.subject }}</label>
              <input
                v-model="subject"
                class="field-input"
                :class="{ 'has-error': hasAttemptedSubmit && !subject.trim() }"
                type="text"
                :placeholder="subjectPlaceholder"
              />
              <span v-if="hasAttemptedSubmit && !subject.trim()" class="field-error">
                {{ STRINGS.feedback.errors.subjectRequired }}
              </span>
            </div>

            <!-- Description -->
            <div class="field">
              <label class="field-label">{{ STRINGS.feedback.labels.description }}</label>
              <textarea
                v-model="description"
                class="field-input"
                :class="{ 'has-error': hasAttemptedSubmit && !description.trim() }"
                :placeholder="descriptionPlaceholder"
              ></textarea>
              <span v-if="hasAttemptedSubmit && !description.trim()" class="field-error">
                {{ STRINGS.feedback.errors.descriptionRequired }}
              </span>
            </div>

            <!-- Contact consent -->
            <label class="contact-consent">
              <input v-model="contactOk" type="checkbox" class="consent-checkbox" />
              <span class="consent-label">{{ STRINGS.feedback.labels.contactOk }}</span>
            </label>

            <!-- Server error -->
            <p v-if="error" class="field-error">{{ error.message }}</p>
          </div>

          <!-- Footer -->
          <div class="feedback-footer">
            <button class="btn btn-ghost" :disabled="isSubmitting" @click="close">
              {{ STRINGS.feedback.buttons.cancel }}
            </button>
            <button
              class="btn btn-submit"
              :class="{ 'btn-submitting': isSubmitting }"
              :disabled="isSubmitting"
              @click="handleSubmit"
            >
              <i v-if="!isSubmitting" class="pi pi-send btn-send-icon"></i>
              {{
                isSubmitting ? STRINGS.feedback.buttons.sending : STRINGS.feedback.buttons.submit
              }}
            </button>
          </div>
        </template>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import STRINGS from '@/constants/strings';
import { useFeedback } from '@/composables/useFeedback';
import type { FeedbackCategory } from '@/types/feedback';

interface Props {
  visible: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  close: [];
}>();

const { isSubmitting, error, isSuccess, submitFeedback, reset } = useFeedback();

const category = ref<FeedbackCategory>('bug');
const subject = ref('');
const description = ref('');
const contactOk = ref(false);
const hasAttemptedSubmit = ref(false);

const categoryOptions = [
  {
    value: 'bug' as FeedbackCategory,
    label: STRINGS.feedback.categories.bug,
    icon: 'pi pi-exclamation-circle',
  },
  {
    value: 'idea' as FeedbackCategory,
    label: STRINGS.feedback.categories.idea,
    icon: 'pi pi-lightbulb',
  },
  {
    value: 'other' as FeedbackCategory,
    label: STRINGS.feedback.categories.other,
    icon: 'pi pi-comments',
  },
];

const subjectPlaceholder = computed(() => {
  const map: Record<FeedbackCategory, string> = {
    bug: STRINGS.feedback.placeholders.bugSubject,
    idea: STRINGS.feedback.placeholders.ideaSubject,
    other: STRINGS.feedback.placeholders.otherSubject,
  };
  return map[category.value];
});

const descriptionPlaceholder = computed(() => {
  const map: Record<FeedbackCategory, string> = {
    bug: STRINGS.feedback.placeholders.bugDescription,
    idea: STRINGS.feedback.placeholders.ideaDescription,
    other: STRINGS.feedback.placeholders.otherDescription,
  };
  return map[category.value];
});

function resetForm(): void {
  category.value = 'bug';
  subject.value = '';
  description.value = '';
  contactOk.value = false;
  hasAttemptedSubmit.value = false;
  reset();
}

function close(): void {
  if (!isSubmitting.value) {
    emit('close');
  }
}

async function handleSubmit(): Promise<void> {
  hasAttemptedSubmit.value = true;

  const subjectEmpty = !subject.value.trim();
  const descriptionEmpty = !description.value.trim();
  if (subjectEmpty || descriptionEmpty) {
    return;
  }

  await submitFeedback({
    category: category.value,
    subject: subject.value.trim(),
    description: description.value.trim(),
    ...(contactOk.value ? { contact_ok: true } : {}),
  });
}

// Reset form when modal opens
watch(
  () => props.visible,
  newVal => {
    if (newVal) {
      resetForm();
    }
  }
);
</script>

<style scoped>
.feedback-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(10, 10, 15, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 9000;
}

.feedback-panel {
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
  background: var(--color-arena-surface);
  border: 2px solid rgba(201, 168, 76, 0.25);
  border-radius: var(--radius-card);
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.5),
    0 0 60px rgba(201, 168, 76, 0.06);
}

/* --- Header --- */
.feedback-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem 1rem;
  border-bottom: 1px solid var(--color-arena-border);
}

.feedback-header-left {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}

.feedback-header-icon {
  font-size: 1.125rem;
  color: var(--color-energy-primary);
}

.feedback-title {
  font-family: var(--font-display);
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--color-arena-text-bright);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin: 0;
}

.feedback-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: transparent;
  border: none;
  color: var(--color-arena-text-muted);
  cursor: pointer;
  border-radius: 50%;
  transition:
    color var(--animate-duration-fast) ease,
    background var(--animate-duration-fast) ease;
}

.feedback-close:hover:not(:disabled) {
  color: var(--color-arena-text-bright);
  background: rgba(255, 255, 255, 0.05);
}

.feedback-close:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

/* --- Body --- */
.feedback-body {
  padding: 1.25rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  transition: opacity var(--animate-duration-fast) ease;
}

.feedback-body--submitting {
  opacity: 0.5;
  pointer-events: none;
}

.feedback-prompt {
  font-family: var(--font-sans);
  font-size: 0.8125rem;
  color: var(--color-arena-text-muted);
  line-height: 1.5;
  margin: 0;
}

/* --- Type Selector --- */
.type-selector {
  display: flex;
  gap: 0.5rem;
}

.type-option {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.375rem;
  padding: 0.75rem 0.5rem;
  background: transparent;
  border: 1px solid var(--color-arena-border);
  border-radius: var(--radius-input);
  color: var(--color-arena-text-muted);
  cursor: pointer;
  transition:
    border-color var(--animate-duration-fast) ease,
    background-color var(--animate-duration-fast) ease,
    color var(--animate-duration-fast) ease;
  font-family: var(--font-sans);
}

.type-option:hover {
  border-color: var(--color-arena-text-dim);
  color: var(--color-arena-text-bright);
}

.type-option.active {
  border-color: var(--color-energy-primary);
  background: rgba(201, 168, 76, 0.06);
  color: var(--color-energy-primary);
}

.type-option i {
  font-size: 1.125rem;
}

.type-option span {
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

/* --- Form Fields --- */
.field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.field-label {
  font-family: var(--font-sans);
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--color-arena-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.field-input {
  font-family: var(--font-sans);
  font-size: 0.875rem;
  color: var(--color-arena-text-bright);
  background: var(--color-arena-void);
  border: 1px solid var(--color-arena-border);
  border-radius: var(--radius-input);
  padding: 0.625rem 0.75rem;
  outline: none;
  transition: border-color var(--animate-duration-fast) ease;
}

.field-input:focus {
  border-color: var(--color-energy-primary);
  box-shadow: 0 0 0 2px rgba(201, 168, 76, 0.15);
}

.field-input::placeholder {
  color: var(--color-arena-text-dim);
}

textarea.field-input {
  resize: vertical;
  min-height: 100px;
  line-height: 1.5;
}

/* --- Contact Consent --- */
.contact-consent {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.consent-checkbox {
  appearance: none;
  -webkit-appearance: none;
  width: 16px;
  height: 16px;
  border: 1px solid var(--color-arena-border);
  border-radius: 3px;
  background: var(--color-arena-void);
  cursor: pointer;
  position: relative;
  flex-shrink: 0;
  transition:
    border-color var(--animate-duration-fast) ease,
    background var(--animate-duration-fast) ease;
}

.consent-checkbox:checked {
  background: var(--color-energy-primary);
  border-color: var(--color-energy-primary);
}

.consent-checkbox:checked::after {
  content: '';
  position: absolute;
  left: 4px;
  top: 1px;
  width: 5px;
  height: 9px;
  border: solid var(--color-arena-void);
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.consent-label {
  font-family: var(--font-sans);
  font-size: 0.8125rem;
  color: var(--color-arena-text-muted);
}

.field-input.has-error {
  border-color: var(--color-energy-danger);
}

.field-error {
  font-size: 0.6875rem;
  color: var(--color-energy-danger);
}

/* --- Footer --- */
.feedback-footer {
  padding: 1rem 1.5rem 1.25rem;
  border-top: 1px solid var(--color-arena-border);
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  min-height: 44px;
  font-family: var(--font-sans);
  font-size: 0.8125rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-radius: var(--radius-button);
  cursor: pointer;
  transition:
    transform var(--animate-duration-fast) ease,
    background-color var(--animate-duration-fast) ease,
    border-color var(--animate-duration-fast) ease,
    color var(--animate-duration-fast) ease,
    box-shadow var(--animate-duration-fast) ease,
    filter var(--animate-duration-fast) ease;
}

.btn-ghost {
  background: transparent;
  border: none;
  color: var(--color-arena-text-muted);
}

.btn-ghost:hover:not(:disabled) {
  color: var(--color-arena-text-bright);
}

.btn-ghost:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.btn-submit {
  background: linear-gradient(135deg, var(--color-primary-600), var(--color-primary-500));
  border: none;
  color: var(--color-arena-void);
}

.btn-submit:hover:not(:disabled) {
  background: linear-gradient(135deg, var(--color-primary-700), var(--color-primary-600));
}

.btn-submit:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.btn-send-icon {
  font-size: 0.75rem;
}

/* --- Submitting spinner --- */
.btn-submitting {
  position: relative;
  color: transparent;
}

.btn-submitting::after {
  content: '';
  position: absolute;
  width: 16px;
  height: 16px;
  border: 2px solid var(--color-arena-void);
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 600ms linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* --- Success State --- */
.success-body {
  padding: 2.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  text-align: center;
}

.success-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(0, 196, 143, 0.1);
  border: 2px solid rgba(0, 196, 143, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: var(--color-energy-success);
  animation: success-pop 400ms var(--ease-bounce) both;
}

@keyframes success-pop {
  0% {
    transform: scale(0.5);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.success-title {
  font-family: var(--font-display);
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--color-arena-text-bright);
  letter-spacing: 0.04em;
  margin: 0;
}

.success-message {
  font-size: 0.8125rem;
  color: var(--color-arena-text-muted);
  line-height: 1.6;
  max-width: 300px;
  margin: 0;
}

.success-dismiss {
  margin-top: 0.5rem;
}

/* --- Transitions --- */
.feedback-fade-enter-active,
.feedback-fade-leave-active {
  transition: opacity 200ms ease;
}

.feedback-fade-enter-active .feedback-panel,
.feedback-fade-leave-active .feedback-panel {
  transition: transform 200ms ease;
}

.feedback-fade-enter-from,
.feedback-fade-leave-to {
  opacity: 0;
}

.feedback-fade-enter-from .feedback-panel {
  transform: scale(0.95);
}

.feedback-fade-leave-to .feedback-panel {
  transform: scale(0.95);
}

@media (prefers-reduced-motion: reduce) {
  .feedback-fade-enter-active,
  .feedback-fade-leave-active {
    transition: none;
  }

  .feedback-fade-enter-active .feedback-panel,
  .feedback-fade-leave-active .feedback-panel {
    transition: none;
  }

  .success-icon {
    animation: none;
  }
}
</style>
